import { CardContent, Card, Typography, Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Dispatch, SetStateAction } from "react";

import { CustomerAppLabel } from "../../components/CustomerAppLabel";
import { DateTimeLabel } from "../../components/DateTimeLabel";
import { PayerInfoLabel } from "../../components/PayerInfoLabel";
import { PaymentMethodLabel } from "../../components/PaymentMethodLabel";
import { PaymentMethodFilter } from "../../components/PaymentMethodFilter";
import { PaymentStatusFilter } from "../../components/PaymentStatusFilter";
import { PaymentStatusLabel } from "../../components/PaymentStatusLabel";
import TableDataGrid from "../../components/TableDataGrid";
import { formatCurrencyToShow } from "../../helpers/currency";
import { formatTransactionFee } from "../../helpers/transaction";
import { ApiPaginated } from "../../interfaces/paginated";
import { Transaction } from "../../interfaces/transaction";

const columns: GridColDef<Transaction>[] = [
    {
        field: "customer.name",
        headerName: "App",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => (
            <CustomerAppLabel
                customer={row.customer?.name || ""}
                app={row.customer?.project_name || ""}
            />
        ),
    },
    {
        field: "payer_info",
        headerName: "Cliente",
        flex: 2,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }) => (
            <PayerInfoLabel
                name={row.payer_name || ""}
                email={row.payer_email || ""}
                document={row.payer_document || ""}
            />
        ),
    },
    {
        field: "payment_method",
        headerName: "Tipo",
        renderCell: (params) => <PaymentMethodLabel value={params.value} />,
        flex: 1,
        minWidth: 130,
        sortable: false,
    },
    {
        field: "acquirer.name",
        headerName: "Adiquirente",
        renderCell: ({ row }) => row.acquirer?.name || "",
        flex: 1,
        minWidth: 140,
        sortable: false,
    },
    {
        field: "amount",
        headerName: "Valor",
        renderCell: ({ value }) => `R$${formatCurrencyToShow(value)}`,
        flex: 1,
        minWidth: 110,
        sortable: false,
    },
    {
        field: "fee",
        headerName: "Taxa",
        renderCell: ({ row }) => {
            return formatTransactionFee(row?.fee_percentage, row?.fixed_fee);
        },
        flex: 1,
        minWidth: 120,
        sortable: false,
    },
    {
        field: "liquid_amount",
        headerName: "Valor Líquido",
        renderCell: ({ value }) => `R$${formatCurrencyToShow(value)}`,
        flex: 1,
        minWidth: 130,
        sortable: false,
    },
    {
        field: "status",
        headerName: "Status",
        renderCell: (params) => <PaymentStatusLabel value={params.value} />,
        flex: 1,
        minWidth: 130,
        sortable: false,
    },
    {
        field: "created_at",
        headerName: "Data/Hora",
        flex: 1,
        minWidth: 150,
        renderCell({ value }) {
            return <DateTimeLabel value={value} />;
        },
    },
];

interface TransactionsHistoryProps {
    data: Transaction[];
    isLoading: boolean;
    meta: Omit<ApiPaginated<Transaction>, "data">;
    setPage: Dispatch<SetStateAction<number>>;
    status: string;
    onStatusChange: (status: string) => void;
    paymentMethod: string;
    onPaymentMethodChange: (method: string) => void;
}

export function TransactionsHistory({
    data,
    isLoading,
    meta,
    setPage,
    status,
    onStatusChange,
    paymentMethod,
    onPaymentMethodChange,
}: TransactionsHistoryProps) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Typography fontWeight={600}>
                        Histórico de Transações
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                        <PaymentStatusFilter
                            value={status}
                            onChange={onStatusChange}
                        />

                        <PaymentMethodFilter
                            value={paymentMethod}
                            onChange={onPaymentMethodChange}
                        />
                    </Box>
                </Box>

                <div className="containerListTablePagesGlobal">
                    <TableDataGrid
                        rows={data}
                        columns={columns}
                        loading={isLoading}
                        hideColumnMenu
                        hideExports
                        hideFilter
                        paginationMode="server"
                        rowCount={meta.total}
                        page={meta.current_page - 1}
                        onPageChange={(newPage) => setPage(newPage + 1)}
                        pageSize={meta.per_page}
                    />
                </div>
            </CardContent>
        </Card>
    );
}
